import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import apiService from "../services/apiService";

function LessonResources() {
  const { lessonId } = useParams();
  const navigate = useNavigate();
  const [lesson, setLesson] = useState(null);
  const [questionSets, setQuestionSets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("resources");
  const [questionSetsLoading, setQuestionSetsLoading] = useState(false);
  const csvUploadRef = useRef(null);
  const qpaperUploadRef = useRef(null);
  const [pollingQuestionSets, setPollingQuestionSets] = useState(new Set());
  // Apr 29 fix (bug #3): non-blocking progress indicator for QPaper upload + audit re-run.
  const [uploadStatus, setUploadStatus] = useState(null);  // { kind, message } or null
  
  const pollingIntervalsRef = useRef({});

  useEffect(() => {
    async function fetchLessonDetails() {
      setLoading(true);
      setError(null);
      try {
        const lessonData = await apiService.getLessonById(lessonId);
        setLesson(lessonData);
      } catch (err) {
        setError("Failed to load lesson resources.");
        console.error(err);
      } finally {
        setLoading(false);
      }
    }
    fetchLessonDetails();
  }, [lessonId]);

  useEffect(() => {
    async function fetchQuestionSets() {
      if (activeTab === "questions") {
        setQuestionSetsLoading(true);
        try {
          const data = await apiService.getQuestionSetsByLesson(lessonId);
          setQuestionSets(data);
          
          // Check for any processing question sets and start polling
          // The field is 'job_status' not 'status'
          data.forEach(qs => {
            if (qs.job_status === 'processing' || qs.job_status === 'pending') {
              startPollingForQuestionSet(qs.id);
            }
          });
        } catch (err) {
          console.error("Failed to load question sets:", err);
          setQuestionSets([]);
        } finally {
          setQuestionSetsLoading(false);
        }
      }
    }
    fetchQuestionSets();
  }, [activeTab, lessonId]);

  // Cleanup polling intervals on unmount
  useEffect(() => {
    return () => {
      Object.values(pollingIntervalsRef.current).forEach(intervalId => {
        clearInterval(intervalId);
      });
      pollingIntervalsRef.current = {};
    };
  }, []);

  const startPollingForQuestionSet = (questionSetId) => {
    // Avoid duplicate polling
    if (pollingIntervalsRef.current[questionSetId]) {
      return;
    }

    console.log(`Starting polling for question set ${questionSetId}`);
    setPollingQuestionSets(prev => new Set(prev).add(questionSetId));

    const pollStatus = async () => {
      try {
        const response = await apiService.checkQuestionGenerationStatus(
          lessonId,
          questionSetId
        );

        if (response.success) {
          const { status, question_set } = response.data;

          if (status === 'completed') {
            console.log(`✅ Question set ${questionSetId} generation complete!`);
            
            // Clear polling
            if (pollingIntervalsRef.current[questionSetId]) {
              clearInterval(pollingIntervalsRef.current[questionSetId]);
              delete pollingIntervalsRef.current[questionSetId];
            }
            setPollingQuestionSets(prev => {
              const newSet = new Set(prev);
              newSet.delete(questionSetId);
              return newSet;
            });

            // Update the question set job_status in the list
            setQuestionSets(prevSets => 
              prevSets.map(qs => 
                qs.id === questionSetId 
                  ? { ...qs, job_status: 'completed', question_count: response.data.count || qs.question_count }
                  : qs
              )
            );

          } else if (status === 'failed') {
            console.error(`❌ Question set ${questionSetId} generation failed`);
            
            // Clear polling
            if (pollingIntervalsRef.current[questionSetId]) {
              clearInterval(pollingIntervalsRef.current[questionSetId]);
              delete pollingIntervalsRef.current[questionSetId];
            }
            setPollingQuestionSets(prev => {
              const newSet = new Set(prev);
              newSet.delete(questionSetId);
              return newSet;
            });

            // Update the question set job_status to failed
            setQuestionSets(prevSets => 
              prevSets.map(qs => 
                qs.id === questionSetId 
                  ? { ...qs, job_status: 'failed' }
                  : qs
              )
            );

          } else if (status === 'processing' || status === 'pending') {
            // Still processing, continue polling
            console.log(`⏳ Question set ${questionSetId} still processing...`);
            
            // Update job_status if it changed from pending to processing
            setQuestionSets(prevSets => 
              prevSets.map(qs => 
                qs.id === questionSetId && qs.job_status !== status
                  ? { ...qs, job_status: status }
                  : qs
              )
            );
          }
        }
      } catch (err) {
        console.error(`Error polling for question set ${questionSetId}:`, err);
        // Don't stop polling on temporary errors
      }
    };

    // Start polling immediately
    pollStatus();
    
    // Then poll every 5 seconds
    pollingIntervalsRef.current[questionSetId] = setInterval(pollStatus, 5000);
  };

  const getResourceIcon = (filetype) => {
    const type = filetype?.toLowerCase();
    if (type === 'pdf') return '📄';
    if (type === 'mp4' || type === 'video') return '📹';
    if (type === 'txt') return '📝';
    return '🔗';
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const getStatusBadge = (jobStatus) => {
    if (!jobStatus || jobStatus === 'completed') {
      return null;
    }

    const statusConfig = {
      'processing': { emoji: '⏳', text: 'Processing...', className: 'badge-processing' },
      'pending': { emoji: '⌛', text: 'Pending...', className: 'badge-pending' },
      'failed': { emoji: '❌', text: 'Failed', className: 'badge-failed' }
    };

    const config = statusConfig[jobStatus] || { emoji: '❓', text: jobStatus, className: 'badge-unknown' };

    const spinnerStyle = (jobStatus === 'processing' || jobStatus === 'pending') ? {
      display: 'inline-block',
      animation: 'spin 1.5s linear infinite',
      marginRight: '0.25rem'
    } : {};

    return (
      <>
        <style>{`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
          @keyframes pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.5; }
          }
        `}</style>
        <span className={`status-badge ${config.className}`} style={{
          padding: '0.25rem 0.5rem',
          borderRadius: '4px',
          fontSize: '0.85rem',
          marginLeft: '0.5rem',
          backgroundColor: jobStatus === 'failed' ? '#dc3545' : '#ffc107',
          color: jobStatus === 'failed' ? 'white' : '#000',
          animation: (jobStatus === 'processing' || jobStatus === 'pending') ? 'pulse 2s ease-in-out infinite' : 'none'
        }}>
          <span style={spinnerStyle}>{config.emoji}</span>
          {config.text}
        </span>
      </>
    );
  };

  // Format the upload-result alert. Surfaces backend's per-row drop reasons
  // (sent in `dropped` array) so users see "0 of 5 — row 2: no question text"
  // instead of a silent zero.
  const formatUploadResult = (data, fallbackName, mode) => {
    const created = data?.created ?? 0;
    const dropped = data?.dropped || [];
    const headers = data?.headers_seen || [];
    const verb = mode === 'qpaper' ? 'Extracted' : 'Uploaded';
    let msg = `${verb}: ${created} question(s)`;
    if (fallbackName) msg += ` in "${fallbackName}"`;
    if (dropped.length > 0) {
      msg += `\n\nSkipped ${dropped.length} row(s):\n`;
      msg += dropped.slice(0, 5).map(d => `  row ${d.row}: ${d.reason}`).join('\n');
      if (dropped.length > 5) msg += `\n  …and ${dropped.length - 5} more`;
      if (headers.length) msg += `\n\nColumns we detected: ${headers.join(', ')}`;
      msg += `\n\nTip: click "Download Template" for a working example.`;
    }
    return msg;
  };

  const handleUploadCSV = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const name = prompt('Question set name:', file.name.replace('.csv', ''));
    if (!name) return;
    try {
      const data = await apiService.uploadQuestionSetCsv(lessonId, file, name);
      alert(formatUploadResult(data, name, 'csv'));
      // Refresh question sets
      const qsData = await apiService.getQuestionSetsByLessonQuery(lessonId);
      setQuestionSets(qsData?.results || qsData || []);
      setActiveTab('questions');
    } catch (err) {
      alert('Upload failed: ' + err.message);
    }
    e.target.value = '';
  };

  const handleUploadQPaper = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const name = prompt('Question set name:', file.name.replace(/\.(pdf|docx?|csv)$/i, ''));
    if (!name) return;
    try {
      const ext = file.name.split('.').pop().toLowerCase();
      const needsAi = ext === 'pdf' || ext === 'doc' || ext === 'docx';
      // Non-blocking inline banner instead of a modal alert (bug #3 from Apr 29 list).
      setUploadStatus({
        kind: 'qpaper',
        message: needsAi
          ? `Uploading "${file.name}" and extracting questions with AI… this may take 30-60 seconds.`
          : `Uploading "${file.name}"…`,
      });
      const data = await apiService.uploadQuestionPaper(lessonId, file, name);
      setUploadStatus(null);
      const summary = formatUploadResult(data, file.name, 'qpaper');
      alert(`${summary}${data.source_type ? ` (${data.source_type})` : ''}`);
      const qsData = await apiService.getQuestionSetsByLessonQuery(lessonId);
      setQuestionSets(qsData?.results || qsData || []);
      setActiveTab('questions');
    } catch (err) {
      setUploadStatus(null);
      alert('Upload failed: ' + err.message);
    }
    e.target.value = '';
  };

  const handleDownloadTemplate = async (kind) => {
    try {
      await apiService.downloadQuestionSetTemplate(kind);
    } catch (err) {
      alert('Could not download template: ' + err.message);
    }
  };

  const renderResourcesTab = () => {
    const hasDocResources = lesson.doc_resources && lesson.doc_resources.filter(r => r.category === 'lesson').length > 0;
    const hasSummary = lesson.summary && lesson.summary.trim().length > 0;
    const hasTranscript = lesson.audio_transcript && lesson.audio_transcript.trim().length > 0;
    const hasFile = lesson.file && lesson.file.trim().length > 0;
    const hasContent = hasSummary || hasTranscript || hasFile || hasDocResources;

    if (hasDocResources) {
      // Original behavior: show doc resources list
      return (
        <div>
          {lesson.doc_resources
            .filter(resource => resource.category === 'lesson')
            .map(resource => (
              <div key={resource.id} className="lesson-card">
                <h4>{getResourceIcon(resource.filetype)} {resource.filename}</h4>
                <div className="quick-actions">
                  <button className="btn btn-primary" onClick={() => navigate(`/lessons/${lessonId}/resource/${resource.id}`)}>
                    View Resource
                  </button>
                </div>
              </div>
            ))
          }
        </div>
      );
    }

    // Two-panel layout matching LessonResourceDetail style
    return (
      <div>
        <div className="resource-detail-container" style={{ display: 'flex', gap: '20px', marginTop: '16px' }}>
          {/* Left panel: Summary + Transcript */}
          <div className="resource-left-panel" style={{ flex: 1, minWidth: 0 }}>
            <div className="course-detail-tabs" style={{ marginBottom: '0' }}>
              <div className="course-detail-tab active">Summary</div>
            </div>
            <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '0 0 8px 8px', padding: '20px', minHeight: '500px' }}>
              {hasSummary ? (
                <div>
                  <div style={{ fontSize: '14px', lineHeight: 1.8, color: '#333', whiteSpace: 'pre-wrap' }}>
                    {lesson.summary}
                  </div>
                  <div style={{ display: 'flex', gap: '8px', marginTop: '16px', paddingTop: '12px', borderTop: '1px solid #e2e8f0' }}>
                    <button className="btn btn-primary" onClick={async () => {
                      try {
                        await apiService.summarizeLesson(lessonId, null);
                        alert('Summary generation started. Refresh in a few seconds.');
                      } catch (e) {
                        alert('Summarize requires an uploaded document. Use Upload Content first.');
                      }
                    }}>
                      Re-generate Summary
                    </button>
                  </div>
                </div>
              ) : (
                <div>
                  <p style={{ color: '#888', marginBottom: '16px' }}>No summary available yet.</p>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button className="btn btn-primary" onClick={async () => {
                      try {
                        await apiService.summarizeLesson(lessonId, null);
                        alert('Summary generation started. Refresh in a few seconds.');
                      } catch (e) {
                        alert('Summarize requires an uploaded document. Use Upload Content first.');
                      }
                    }}>
                      Create Summary
                    </button>
                    <button className="btn btn-secondary" onClick={() => navigate(`/upload?courseId=${lesson.course}&lessonId=${lessonId}`)}>
                      Upload Content
                    </button>
                  </div>
                </div>
              )}
              {hasTranscript && (
                <div style={{ marginTop: '24px', paddingTop: '16px', borderTop: '1px solid #e2e8f0' }}>
                  <h4 style={{ color: '#042F54', marginBottom: '8px' }}>Transcript</h4>
                  <div style={{ fontSize: '13px', lineHeight: 1.7, color: '#555', whiteSpace: 'pre-wrap', maxHeight: '300px', overflowY: 'auto' }}>
                    {lesson.audio_transcript}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Right panel: Document viewer */}
          <div className="resource-right-panel" style={{ flex: 1, minWidth: 0 }}>
            {hasFile ? (
              <div className="document-viewer-container" style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px', overflow: 'hidden' }}>
                <div style={{ padding: '10px 16px', background: '#f8f9fa', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: '13px', color: '#555' }}>Document Preview</span>
                  <a href={lesson.file} target="_blank" rel="noopener noreferrer" className="btn btn-primary" style={{ fontSize: '12px', padding: '4px 12px' }}>
                    Download
                  </a>
                </div>
                <iframe
                  src={`https://docs.google.com/viewer?url=${encodeURIComponent(lesson.file)}&embedded=true`}
                  title={lesson.title}
                  width="100%"
                  height="600px"
                  style={{ border: 'none' }}
                />
              </div>
            ) : (
              <div style={{ background: '#f8f9fa', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '40px', minHeight: '500px', display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center' }}>
                <div>
                  <div style={{ fontSize: '48px', marginBottom: '16px' }}>📄</div>
                  <p style={{ color: '#666', fontSize: '16px', fontWeight: 600 }}>No document uploaded yet</p>
                  <p style={{ color: '#999', fontSize: '13px', marginTop: '8px', maxWidth: '280px' }}>Upload a PDF, PPTX, or video file to preview it here and enable AI summarization</p>
                  <button className="btn btn-primary" style={{ marginTop: '20px', padding: '10px 24px' }}
                    onClick={() => navigate(`/upload?courseId=${lesson.course}&lessonId=${lessonId}`)}>
                    📤 Upload Content
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderQuestionsTab = () => {
    if (questionSetsLoading) {
      return <p>Loading question sets...</p>;
    }

    if (!questionSets || questionSets.length === 0) {
      return <p>No question sets found for this lesson.</p>;
    }

    return (
      <div>
        {questionSets.map(questionSet => {
          const isProcessing = questionSet.job_status === 'processing' || questionSet.job_status === 'pending';
          const isFailed = questionSet.job_status === 'failed';
          
          return (
            <div key={questionSet.id} className="lesson-card">
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <h4>📝 {questionSet.name || questionSet.title}</h4>
                {getStatusBadge(questionSet.job_status)}
              </div>
              <div style={{ marginTop: "0.5rem", color: "var(--text-light)", fontSize: "0.9rem" }}>
                <p>Questions: {questionSet.question_count || 0}</p>
                <p>Answer Style: {questionSet.answer_style}</p>
                <p>Created: {formatDate(questionSet.created_at)}</p>
              </div>
              <div className="quick-actions">
                <button
                  className="btn btn-primary"
                  onClick={() => navigate(`/question-sets/${questionSet.id}`)}
                  disabled={isProcessing}
                  style={isProcessing ? { opacity: 0.6, cursor: 'not-allowed' } : {}}
                >
                  {isProcessing ? (
                    <>
                      <span style={{
                        display: 'inline-block',
                        animation: 'spin 1s linear infinite',
                        marginRight: '0.5rem'
                      }}>⚙️</span>
                      Processing...
                    </>
                  ) : isFailed ? 'View (Failed)' : 'View Question Set'}
                </button>
                {questionSet?.generation_metadata?.source_file_url && (
                  <button
                    className="btn btn-secondary"
                    title={`Download original uploaded file: ${questionSet.generation_metadata.source_file_name || 'source'}`}
                    onClick={async () => {
                      try {
                        const data = await apiService.getQuestionSetSourceFileUrl(questionSet.id);
                        if (data?.url) {
                          window.open(data.url, '_blank', 'noopener,noreferrer');
                        } else {
                          alert('No source file available for this question set.');
                        }
                      } catch (e) {
                        alert('Could not get download link: ' + (e?.message || 'unknown error'));
                      }
                    }}
                  >
                    ⬇ Download Source
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  if (loading) return <div>Loading lesson resources...</div>;
  if (error) return <div>{error}</div>;
  if (!lesson) return <div>No lesson found.</div>;

  return (
    <div>
      {uploadStatus && (
        <div style={{
          position: 'fixed', top: 12, left: '50%', transform: 'translateX(-50%)',
          background: '#fff7ed', color: '#9a3412', border: '1px solid #fed7aa',
          padding: '0.75rem 1.25rem', borderRadius: 8, zIndex: 9999,
          boxShadow: '0 4px 16px rgba(0,0,0,0.08)', display: 'flex', alignItems: 'center', gap: '0.75rem',
          maxWidth: '90vw'
        }}>
          <div style={{
            width: 18, height: 18, borderRadius: '50%',
            border: '2px solid #fed7aa', borderTopColor: '#9a3412',
            animation: 'spin 0.9s linear infinite'
          }} />
          <span>{uploadStatus.message}</span>
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      )}
      <div className="course-detail-header">
        <button className="btn btn-secondary" onClick={() => navigate(`/course/${lesson.course}`)}>
          ← Back to Course
        </button>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginTop: "1rem" }}>
          <h1>{lesson.title}</h1>
          <div style={{ display: 'flex', gap: '1rem', marginLeft: '1rem' }}>
            <button 
              className="btn btn-primary" 
              onClick={() => navigate(`/upload?courseId=${lesson.course}&lessonId=${lessonId}`)}
            >
              📤 Upload Content
            </button>
            <button
              className="btn btn-primary"
              onClick={() => navigate(`/quiz?courseId=${lesson.course}&lessonId=${lessonId}`)}
            >
              📝 Create Question Set
            </button>
            <button
              className="btn"
              style={{ background: '#28a745', color: '#fff' }}
              onClick={() => csvUploadRef.current?.click()}
            >
              📤 Upload CSV
            </button>
            <input
              ref={csvUploadRef}
              type="file"
              accept=".csv"
              onChange={handleUploadCSV}
              style={{ display: 'none' }}
            />
            <button
              className="btn"
              style={{ background: '#F56A3F', color: '#fff' }}
              onClick={() => qpaperUploadRef.current?.click()}
            >
              Upload Question Paper
            </button>
            <input
              ref={qpaperUploadRef}
              type="file"
              accept=".csv,.pdf,.doc,.docx"
              onChange={handleUploadQPaper}
              style={{ display: 'none' }}
            />
            <button
              className="btn btn-outline"
              style={{ border: '1px solid #ccc', color: '#555' }}
              title="Download a CSV with the exact column headers our parser expects, plus example rows. Edit it in Excel and upload."
              onClick={() => handleDownloadTemplate('qpaper')}
            >
              📥 Download Template
            </button>
          </div>
        </div>
      </div>

      <div className="course-detail-tabs">
        <div 
          className={`course-detail-tab ${activeTab === "resources" ? "active" : ""}`} 
          onClick={() => setActiveTab("resources")}
        >
          Lesson Resources
        </div>
        <div 
          className={`course-detail-tab ${activeTab === "questions" ? "active" : ""}`} 
          onClick={() => setActiveTab("questions")}
        >
          Question Sets
        </div>
      </div>

      <div id="course-detail-tab-content">
        {activeTab === "resources" && renderResourcesTab()}
        {activeTab === "questions" && renderQuestionsTab()}
      </div>
    </div>
  );
}

export default LessonResources;